// Phase 129 Plan 09 (Codex review hardening) — SignupPage.
//
// Entry point for the marketing site → app frontend handoff. The marketing
// site (`web/app/lib/handoff.ts::buildSignupUrl`) generates
//   https://app.hookmyapp.com/signup?handoff=<jwt>
// after `POST /demo/handoff` mints a 10-minute HS256 JWT carrying the demo
// phone. This page:
//
//   1. Reads `?handoff=<jwt>` from the URL via `useSearchParams`.
//   2. Persists the JWT to sessionStorage (one-shot — see handoff-storage.ts).
//   3. Fires WorkOS AuthKit's `signUp()` flow.
//   4. Renders a brief Loader while the redirect kicks in.
//
// The page MUST live OUTSIDE `<ProtectedRoute>` because the user is
// unauthenticated when they land here. After AuthKit completes its hosted
// signup flow, WorkOS redirects to `/callback`, which redirects to `/`,
// which (via `RootRedirect` + `WorkspaceProvider`) issues the FIRST authed
// backend request — at which point `useFetch` / WorkspaceProvider's
// internal `authFetch` reads the stored JWT and attaches `X-Handoff-JWT`.

import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { useSession } from '@/contexts/session-context';
import { setHandoffJwt } from '@/lib/handoff-storage';

export function SignupPage() {
  const [searchParams] = useSearchParams();
  const { signIn, isLoading } = useSession();

  useEffect(() => {
    // Persist BEFORE triggering signIn so the JWT survives the WorkOS
    // round-trip. The cookie session cookie lives on the api-host, while
    // sessionStorage stays on the app-host across the redirect — so the
    // JWT is readable on the first authed call back in the app.
    const jwt = searchParams.get('handoff');
    if (jwt && jwt.length > 0) {
      setHandoffJwt(jwt);
    }

    // Wait for /auth/me to resolve before navigating, so an authed return
    // visit isn't bounced back through login redundantly.
    if (isLoading) return;

    // WorkOS hosted UI presents both sign-in and sign-up tabs; one entry
    // point covers both flows under cookie auth.
    signIn();
  }, [isLoading, searchParams, signIn]);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-base text-muted-foreground">Redirecting to sign up...</p>
    </div>
  );
}
